import React, { useEffect, useRef, useState } from 'react';

/**
 * Lightweight Leaflet + OpenStreetMap pin picker. No paid Google Maps
 * dependency. Renders in ~200px height. Emits `onChange({lat, lng})`
 * whenever the pin moves. Also accepts manual coordinate changes via
 * the `lat` / `lng` props so both inputs stay in sync.
 */
export default function BranchMapPicker({ lat, lng, onChange, height = 240 }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [ready, setReady] = useState(false);

  const hasCoords = Number.isFinite(Number(lat)) && Number.isFinite(Number(lng));
  const initialCentre = hasCoords ? [Number(lat), Number(lng)] : [14.5995, 120.9842]; // Metro Manila default
  const initialZoom = hasCoords ? 14 : 3;

  // Load Leaflet CSS lazily (avoids bundling into shared css)
  useEffect(() => {
    const cssId = 'leaflet-css';
    if (!document.getElementById(cssId)) {
      const link = document.createElement('link');
      link.id = cssId;
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }
    setReady(true);
  }, []);

  // Initialise Leaflet only once
  useEffect(() => {
    if (!ready || mapRef.current || !containerRef.current) return;
    let cancelled = false;

    (async () => {
      const L = (await import('leaflet')).default || (await import('leaflet'));
      if (cancelled) return;

      // Marker icon fix — Leaflet's default paths break under bundlers.
      delete L.Icon.Default.prototype._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
        iconUrl:       'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
        shadowUrl:     'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
      });

      const map = L.map(containerRef.current, { scrollWheelZoom: false })
        .setView(initialCentre, initialZoom);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution:
          '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      }).addTo(map);

      if (hasCoords) {
        markerRef.current = L.marker(initialCentre, { draggable: true }).addTo(map);
        markerRef.current.on('dragend', (e) => {
          const { lat: la, lng: lo } = e.target.getLatLng();
          onChange && onChange({ lat: Number(la.toFixed(6)), lng: Number(lo.toFixed(6)) });
        });
      }

      map.on('click', (e) => {
        const { lat: la, lng: lo } = e.latlng;
        if (!markerRef.current) {
          markerRef.current = L.marker([la, lo], { draggable: true }).addTo(map);
          markerRef.current.on('dragend', (ev) => {
            const p = ev.target.getLatLng();
            onChange && onChange({ lat: Number(p.lat.toFixed(6)), lng: Number(p.lng.toFixed(6)) });
          });
        } else {
          markerRef.current.setLatLng([la, lo]);
        }
        onChange && onChange({ lat: Number(la.toFixed(6)), lng: Number(lo.toFixed(6)) });
      });

      mapRef.current = map;
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  // Push manual lat/lng changes into the map
  useEffect(() => {
    if (!mapRef.current) return;
    if (!hasCoords) return;
    const p = [Number(lat), Number(lng)];
    if (!markerRef.current) {
      // Marker library import is async; retry once via microtask.
      import('leaflet').then((mod) => {
        const L = mod.default || mod;
        markerRef.current = L.marker(p, { draggable: true }).addTo(mapRef.current);
        markerRef.current.on('dragend', (e) => {
          const q = e.target.getLatLng();
          onChange && onChange({ lat: Number(q.lat.toFixed(6)), lng: Number(q.lng.toFixed(6)) });
        });
      });
    } else {
      markerRef.current.setLatLng(p);
    }
    mapRef.current.setView(p, Math.max(mapRef.current.getZoom(), 12));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lng]);

  return (
    <div
      ref={containerRef}
      style={{ width: '100%', height }}
      className="rounded-md border border-[#E2E8F0] overflow-hidden bg-[#F8FAFC]"
      data-testid="branch-map-picker"
    />
  );
}
